import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { StandardPageHeader } from '@/components/layout/StandardPageHeader';
import { PageContainer } from '@/components/layout/PageContainer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Brain, AlertTriangle, CreditCard, Clock, Users, Sparkles, ArrowRight } from 'lucide-react';
import { useBillAssistData } from '@/hooks/useBillAssistData';
import { formatCurrency } from '@/lib/format';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

interface SmartCardProps {
  title: string;
  count: number;
  impact: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  suggestion: string;
  confidence: number;
  icon: React.ElementType;
  onAction: () => void;
}

const SmartCard: React.FC<SmartCardProps> = ({
  title,
  count,
  impact,
  priority,
  suggestion,
  confidence,
  icon: Icon,
  onAction
}) => {
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'destructive';
      case 'high': return 'destructive';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'outline';
    }
  };

  const getPriorityBgColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-50 border-red-200';
      case 'high': return 'bg-orange-50 border-orange-200';
      case 'medium': return 'bg-yellow-50 border-yellow-200';
      case 'low': return 'bg-blue-50 border-blue-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <motion.div variants={itemVariants}>
      <Card className={`h-full hover:shadow-lg transition-all duration-200 ${getPriorityBgColor(priority)}`}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <Icon className="h-5 w-5 text-gray-600" />
              <CardTitle className="text-lg">{title}</CardTitle>
            </div>
            <Badge variant={getPriorityColor(priority) as any} className="text-xs">
              {priority.toUpperCase()}
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Metrics */}
          <div className="flex justify-between items-center">
            <div>
              <div className="text-2xl font-bold text-gray-900">{count}</div>
              <div className="text-sm text-gray-500">items</div>
            </div>
            <div className="text-right">
              <div className="text-lg font-semibold text-red-600">{impact}</div>
              <div className="text-sm text-gray-500">at risk</div>
            </div>
          </div>

          {/* AI Suggestion */}
          <div className="bg-white/80 p-3 rounded-lg border">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-blue-500" />
              <span className="text-sm font-medium text-gray-700">AI Recommendation</span>
              <Badge variant="outline" className="text-xs">
                {Math.round(confidence * 100)}% confidence
              </Badge>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">{suggestion}</p>
          </div>

          {/* Actions */}
          {count > 0 && (
            <Button onClick={onAction} className="w-full text-sm">
              Take Action
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

const BillAssistPage: React.FC = () => {
  const { data: billAssistData, isLoading, error } = useBillAssistData();
  const navigate = useNavigate();

  // Get icon component from string name
  const getIconComponent = (iconName: string): React.ElementType => {
    switch (iconName) {
      case 'AlertTriangle': return AlertTriangle;
      case 'CreditCard': return CreditCard;
      case 'Clock': return Clock;
      case 'Users': return Users;
      default: return Brain;
    }
  };

  // Handle action for each category
  const handleCardAction = (category: string) => {
    switch (category) {
      case 'overdue_invoices':
        // Navigate to invoices page with overdue filter
        navigate('/admin/invoices?filter=overdue');
        break;
      case 'payment_failures':
        // Navigate to payments page with failed filter
        navigate('/admin/payments?filter=failed');
        break;
      case 'expiring_cards':
        // Navigate to families page with auto-billing filter
        navigate('/admin/families?view=auto-billing');
        break;
      case 'high_value_risks':
        // Navigate to families page with high-value filter
        navigate('/admin/families?filter=high-value');
        break;
      default:
        console.log(`No action handler for category: ${category}`);
    }
  };

  // Convert smart card data to props format
  const smartCards: SmartCardProps[] = billAssistData?.smartCards.map(card => ({
    title: card.title,
    count: card.count,
    impact: card.impact,
    priority: card.priority,
    suggestion: card.suggestion,
    confidence: card.confidence,
    icon: getIconComponent(card.icon),
    onAction: () => handleCardAction(card.category)
  })) || [];

  // Calculate totals from metrics
  const totalItems = billAssistData?.metrics.total_items || 0;
  const totalAtRisk = billAssistData?.metrics.total_revenue_at_risk || 0;
  const avgConfidence = billAssistData?.metrics.avg_confidence || 0;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <StandardPageHeader
          title="Bill Assist"
          subtitle="Smart payment intelligence and automated assistance"
          icon={Brain}
        />
        <PageContainer maxWidth="full">
          <div className="animate-pulse space-y-6">
            <div className="grid gap-4 md:grid-cols-3 mb-8">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-24 bg-gray-200 rounded"></div>
              ))}
            </div>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-64 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </PageContainer>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <StandardPageHeader
          title="Bill Assist"
          subtitle="Smart payment intelligence and automated assistance"
          icon={Brain}
        />
        <PageContainer maxWidth="full">
          <Card>
            <CardContent className="p-6 text-center">
              <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Unable to Load Bill Assist Data</h3>
              <p className="text-gray-600 mb-4">
                There was an error loading your Bill Assist insights. Please try refreshing the page.
              </p>
              <Button onClick={() => window.location.reload()}>
                Retry
              </Button>
            </CardContent>
          </Card>
        </PageContainer>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <StandardPageHeader
        title="Bill Assist"
        subtitle="Smart payment intelligence and automated assistance"
        icon={Brain}
      />
      
      <PageContainer maxWidth="full">
        <div className="space-y-8">
          {/* Overview Metrics */}
          <motion.div 
            initial="hidden" 
            animate="visible" 
            variants={containerVariants}
            className="grid gap-4 md:grid-cols-3"
          >
            <motion.div variants={itemVariants}>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Items</p>
                      <p className="text-2xl font-bold">{totalItems}</p>
                    </div>
                    <Brain className="h-8 w-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Revenue at Risk</p>
                      <p className="text-2xl font-bold text-red-600">{formatCurrency(totalAtRisk / 100)}</p>
                    </div>
                    <AlertTriangle className="h-8 w-8 text-red-500" />
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">AI Confidence</p>
                      <p className="text-2xl font-bold text-green-600">{Math.round(avgConfidence * 100)}%</p>
                    </div>
                    <Sparkles className="h-8 w-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>

          {/* Smart Cards Grid */}
          <motion.div 
            initial="hidden" 
            animate="visible" 
            variants={containerVariants}
            className="grid gap-6 md:grid-cols-2 lg:grid-cols-2"
          >
            {smartCards.map((card, index) => (
              <SmartCard key={index} {...card} />
            ))}
          </motion.div>

          {/* Coming Soon Section */}
          <motion.div variants={itemVariants}>
            <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
              <CardContent className="p-6 text-center">
                <Brain className="h-12 w-12 text-blue-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">More Intelligence Coming Soon</h3>
                <p className="text-gray-600 mb-4">
                  Advanced AI features including predictive analytics, cross-organization learning, 
                  and automated resolution capabilities are in development.
                </p>
                <Badge variant="outline" className="bg-white">Phase 2: Intelligence Engine</Badge>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </PageContainer>
    </div>
  );
};

export default BillAssistPage;